import React from 'react';
import { parseDesc, tieneRequerimientos } from '../hooks/useEventos';
import { fmtHora, fmtFechaCorta, val, normISO } from '../utils/surgeryHelpers';

const BADGE = {
  pending   : { bg: '#e6f9f8', color: '#038f8a', label: 'Pendiente'  },
  confirmada: { bg: '#fffbeb', color: '#d97706', label: 'Confirmada' },
  cancelada : { bg: '#fdf1f2', color: '#e12f3b', label: 'Cancelada'  },
  historico : { bg: '#f3f4f6', color: '#6b7280', label: 'Realizado'  },
};

const TH = ({ children, align = 'left' }) => (
  <th style={{
    background: '#f9fafb', borderBottom: '1px solid #e5e7eb', color: '#6b7280',
    fontSize: '10px', fontWeight: 700, letterSpacing: '.4px', padding: '9px 10px',
    position: 'sticky', textAlign: align, textTransform: 'uppercase', top: 0, whiteSpace: 'nowrap',
  }}>
    {children}
  </th>
);

const TD = ({ children, align = 'left', mono = false }) => (
  <td style={{
    borderBottom: '1px solid #f3f4f6', color: '#111827', fontSize: '12px',
    fontVariantNumeric: mono ? 'tabular-nums' : undefined, padding: '9px 10px',
    textAlign: align, verticalAlign: 'middle', whiteSpace: 'nowrap',
  }}>
    {children}
  </td>
);

const IconBtn = ({ title, color, bg, onClick, children }) => (
  <button
    title={title}
    onClick={(e) => { e.stopPropagation(); onClick(); }}
    style={{
      alignItems: 'center', background: bg, border: 'none', borderRadius: '6px',
      color, cursor: 'pointer', display: 'inline-flex', fontSize: '12px',
      height: '26px', justifyContent: 'center', width: '26px',
    }}
  >
    {children}
  </button>
);

/**
 * Vista de tabla para Pendientes / Confirmadas / Histórico — misma
 * información y acciones que SurgeryCard, en formato tabular compacto
 * para revisar muchos agendamientos de un vistazo.
 */
const SurgeryTable = ({ docs, tipo, onConfirmar, onCancelar, onEditar, onDetalle, onTiempos, mostrarUnidad }) => {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <TH>Fecha</TH>
            <TH>Horario</TH>
            {mostrarUnidad && <TH>Unidad</TH>}
            <TH>Sala</TH>
            <TH>Médico</TH>
            <TH>Paciente</TH>
            <TH>Procedimiento</TH>
            <TH align="center">Req.</TH>
            <TH align="center">Estatus</TH>
            <TH align="right">Acciones</TH>
          </tr>
        </thead>
        <tbody>
          {docs.map((doc) => {
            const ev       = doc?.event || {};
            const summary  = (ev.summary || '').toLowerCase();
            const fields   = parseDesc(ev.description);

            const esCancelado = summary === 'cancelada';
            const key   = esCancelado ? 'cancelada' : (tipo === 'historico' ? 'historico' : summary || 'pending');
            const badge = BADGE[key] || BADGE.pending;

            // El botón "Realizada" solo aplica una vez que ya pasó la hora de inicio programada
            const yaInicio = ev.start?.dateTime && new Date(normISO(ev.start.dateTime)) <= new Date();

            const quirofano    = val(fields['AppointmentType']) || 'Quirófano';
            const unidad       = val(fields['Unidad Hospitalaroa']) || val(fields['Unidad Hospitalaria']) || '—';
            const medico       = val(fields['Medico']) || '—';
            const paciente     = val(fields['Paciente']) || '—';
            const procedimiento= val(fields['Procedimiento']) || '—';

            return (
              <tr
                key={doc.id}
                onClick={() => onDetalle && onDetalle(doc)}
                style={{ cursor: onDetalle ? 'pointer' : 'default' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <TD mono>{fmtFechaCorta(ev.start?.dateTime)}</TD>
                <TD mono>{fmtHora(ev.start?.dateTime)}–{fmtHora(ev.end?.dateTime)}</TD>
                {mostrarUnidad && (
                  <TD>
                    <span style={{ color: '#7c3aed', fontSize: '11px', fontWeight: 700 }}>🏥 {unidad}</span>
                  </TD>
                )}
                <TD>
                  <span style={{ color: '#039490', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase' }}>{quirofano}</span>
                </TD>
                <TD>
                  <div style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{medico}</div>
                </TD>
                <TD>
                  <div style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{paciente}</div>
                </TD>
                <TD>
                  <div style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{procedimiento}</div>
                </TD>
                <TD align="center">
                  {tieneRequerimientos(doc) ? <span title="Tiene requerimientos especiales">⚠️</span> : '—'}
                </TD>
                <TD align="center">
                  <span style={{ background: badge.bg, borderRadius: '20px', color: badge.color, fontSize: '10px', fontWeight: 800, padding: '3px 9px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {badge.label}
                  </span>
                </TD>
                <TD align="right">
                  <div style={{ display: 'inline-flex', gap: '4px' }}>
                    {tipo === 'pendientes' && !esCancelado && onConfirmar && (
                      <IconBtn title="Confirmar" color="#85c100" bg="#f2f9e6" onClick={() => onConfirmar(doc)}>✓</IconBtn>
                    )}
                    {tipo !== 'historico' && !esCancelado && onEditar && (
                      <IconBtn title="Reagendar" color="#0582a9" bg="#e8f4f8" onClick={() => onEditar(doc)}>✎</IconBtn>
                    )}
                    {tipo !== 'historico' && !esCancelado && onCancelar && (
                      <IconBtn title="Cancelar" color="#e12f3b" bg="#fdf1f2" onClick={() => onCancelar(doc)}>✕</IconBtn>
                    )}
                    {tipo !== 'pendientes' && !esCancelado && onTiempos && yaInicio && (
                      <IconBtn title="Realizada" color="#7c3aed" bg="#f3ecfe" onClick={() => onTiempos(doc)}>✓</IconBtn>
                    )}
                    {onDetalle && (
                      <IconBtn title="Detalles" color="#374151" bg="#f3f4f6" onClick={() => onDetalle(doc)}>⊞</IconBtn>
                    )}
                  </div>
                </TD>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default SurgeryTable;